import React from "react";
import { ProductStructList } from "../structs/ProductStructList";
import { ProductStruct } from "../structs/ProductStruct";
import Paper from "@mui/material/Paper";
import { BASE_URL_API, PRODUCT_BASE_URL_API } from "../Const";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ListItem from "@mui/material/ListItem";
import Chip from "@mui/material/Chip";
import LoadingSplash from "./commoncComponent/LoadingSplash";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

/** TTL for localStorage cache: 5 minutes (in ms) */
const PRODUCT_CACHE_TTL = 15 * 60 * 1000; // 300000

export class ProductComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            productList: [],
            loadingVisible: true,
            visibleGroups: 2, // 👈 NEW
        };

        this.loadingRef = React.createRef();
        this.lazyTimer = null; // 👈 NEW
    }

    // 👇 NEW: lazy load groups of categories
    scheduleLazyGroups = () => {
        if (this.lazyTimer) {
            clearTimeout(this.lazyTimer);
        }

        const STEP = 2; // how many groups to add each tick
        const DELAY = 120; // ms between steps

        const loadNext = () => {
            this.setState((prev) => {
                if (
                    !prev.productList ||
                    prev.productList.length === 0 ||
                    prev.visibleGroups >= prev.productList.length
                ) {
                    return null;
                }

                return {
                    visibleGroups: Math.min(
                        prev.visibleGroups + STEP,
                        prev.productList.length
                    ),
                };
            });

            this.lazyTimer = setTimeout(loadNext, DELAY);
        };

        this.lazyTimer = setTimeout(loadNext, DELAY);
    };

    // 👇 NEW: clear timer
    componentWillUnmount() {
        if (this.lazyTimer) {
            clearTimeout(this.lazyTimer);
        }
    }

    componentDidMount() {
        const { market_id, category_id ,scroll_to_top} = this.props;
        const cacheKey = `hk_products_${market_id}_${category_id}`;

        // 🔹 1) Try to load from localStorage cache
        try {
            const cachedStr = localStorage.getItem(cacheKey);
            if (cachedStr) {
                const cached = JSON.parse(cachedStr);

                if (
                    cached &&
                    Array.isArray(cached.data) &&
                    typeof cached.timestamp === "number" &&
                    Date.now() - cached.timestamp < PRODUCT_CACHE_TTL
                ) {
                    // ✅ Valid cache → use it & skip fetch
                    this.setState(
                        {
                            productList: cached.data,
                            loadingVisible: false,
                            visibleGroups: Math.min(
                                2,
                                (cached.data && cached.data.length) || 0
                            ),
                        },
                        () => {
                            this.loadingRef.current?.hide();
                            this.scheduleLazyGroups();
                        }
                    );
                    return;
                } else {
                    // Expired / invalid cache
                    localStorage.removeItem(cacheKey);
                }
            }
        } catch (e) {
            console.warn("Product localStorage cache error:", e);
        }

        // 🔹 2) No valid cache → fetch from API
        this.loadingRef.current?.show("درحال بارگزاری محصولات...");

        let url = BASE_URL_API + "product?market_id=" + market_id;
        if (category_id !== -1) {
            url += "&category_id=" + category_id;
        }

        fetch(url, {
            headers: {
                Authorization: "Bearer " + localStorage.getItem("pwa_token"),
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((results) => {
                const data = new ProductStructList();

                // گروه‌بندی محصولات بر اساس عنوان دسته (product.category.title)
                results.data.forEach((item) => {
                    if (!item.category) return;

                    let group = data.productList.find(
                        (p) => p.category === item.category.title
                    );

                    if (!group) {
                        group = new ProductStruct();
                        group.category = item.category.title;
                        data.productList.push(group);
                    }

                    group.productList.push(item);
                });

                // مرتب‌سازی محصولات هر گروه: محصولات دارای تصویر اول
                data.productList.forEach((group) => {
                    group.productList.sort((a, b) => {
                        const aHasPic = a.pictures && a.pictures.length > 0;
                        const bHasPic = b.pictures && b.pictures.length > 0;

                        if (aHasPic === bHasPic) return 0;
                        return aHasPic ? -1 : 1;
                    });
                });

                // 🔹 3) Save processed result to localStorage
                try {
                    localStorage.setItem(
                        cacheKey,
                        JSON.stringify({
                            timestamp: Date.now(),
                            data: data.productList,
                        })
                    );
                } catch (e) {
                    // If quota error or something, just log – don't break UI
                    console.warn("Unable to save product cache:", e);
                }

                this.setState(
                    {
                        productList: data.productList,
                        loadingVisible: false,
                        visibleGroups: Math.min(
                            2,
                            (data.productList && data.productList.length) || 0
                        ),
                    },
                    () => {
                        this.loadingRef.current?.hide();
                        this.scheduleLazyGroups();
                    }
                );
            })
            .catch((error) => {
                console.log("error loading products", error);
                this.loadingRef.current?.hide();
                this.setState({ loadingVisible: false });
            });
    }

    scrollToTopIfNeeded = () => {
        const { scroll_to_top } = this.props;

        if (scroll_to_top) {
            try {
                // modern browsers – smooth scroll
                window.scrollTo({ top: 0, behavior: "smooth" });
            } catch (e) {
                // fallback for older ones
                window.scrollTo(0, 0);
            }
        }
    };


    renderProductCard = (product) => {
        const MAX_LENGTH = 20;

        const formatStep = (val) => {
            const num = Number(val);
            if (!isFinite(num) || num <= 0) return "";
            return parseFloat(num.toFixed(4)).toLocaleString("en", {
                maximumFractionDigits: 4,
            });
        };

        const formatTomans = (val) =>
            Number(val || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
            " تومان ";

        return (
            <ListItem
                key={product.id}
                component={Link}
                to={"/product?id=" + product.id}
                onClick={this.scrollToTopIfNeeded}
                sx={{
                    width: 250,
                    px: 0,
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 2,
                        p: 1.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        transition: "transform 120ms ease, box-shadow 120ms ease",
                        "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 3,
                        },
                    }}
                >
                    {/* Image */}
                    <Box
                        sx={{
                            width: 140,
                            height: 140,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            bgcolor: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "center",
                        }}
                    >
                        <Box
                            component="img"
                            alt={product.title}
                            src={
                                product.pictures && product.pictures.length > 0
                                    ? PRODUCT_BASE_URL_API +
                                    product.pictures[0].url
                                    : "hamekara-logo.png"
                            }
                            loading="lazy"      // 👈 NEW (no style change)
                            decoding="async"    // 👈 NEW
                            sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    </Box>

                    {/* Title */}
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            direction: "rtl",
                        }}
                        title={product.title}
                    >
                        {product.title.length > MAX_LENGTH
                            ? `${product.title.substring(0, MAX_LENGTH)}...`
                            : product.title}
                    </Typography>

                    {/* Inventory / تماس بگیرید */}
                    <Typography
                        variant="caption"
                        sx={{
                            lineHeight: 1.2,
                            fontWeight: 500,
                            fontSize: "0.82rem",
                            color:
                                Number(product.step) > 0
                                    ? "#909090"
                                    : "error.main",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            whiteSpace: "nowrap",
                            direction: "rtl",
                            textShadow:
                                Number(product.step) > 0
                                    ? "0 0 4px rgba(0,0,0,0.05)"
                                    : "0 0 6px rgba(255,0,0,0.3)",
                        }}
                    >
                        {Number(product.step) > 0 ? (
                            <>
                                <Inventory2Icon
                                    fontSize="inherit"
                                    sx={{ opacity: 0.8 }}
                                />
                                موجودی:&nbsp;{formatStep(product.step)}
                            </>
                        ) : (
                            "تماس بگیرید"
                        )}
                    </Typography>

                    {/* Market name chip (if exists) */}
                    {product.markets?.name && (
                        <Chip
                            label={product.markets.name}
                            size="small"
                            sx={{
                                display: "inline-flex",
                                width: "fit-content",
                                direction: "rtl",
                                fontSize: 11,
                                height: 22,
                                borderRadius: 1,
                                px: 0.8,
                                bgcolor: "grey.100",
                                color: "text.secondary",
                                "& .MuiChip-label": {
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 0.5,
                                },
                            }}
                            title={product.markets.name}
                        />
                    )}

                    {/* Price / Discount */}
                    {Number(product.del_price) > 0 ? (
                        <Box sx={{ mt: "auto" }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    textDecoration: "line-through",
                                    whiteSpace: "nowrap",
                                    direction: "rtl",
                                }}
                            >
                                <strong>{formatTomans(product.price)}</strong>
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: "success.main",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    direction: "rtl",
                                }}
                            >
                                <strong>
                                    {formatTomans(
                                        parseInt(product.price || 0, 10) -
                                        parseInt(
                                            product.del_price || 0,
                                            10
                                        )
                                    )}
                                </strong>
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ mt: "auto" }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    color:
                                        Number(product.price) > 0
                                            ? "success.main"
                                            : "warning.main",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    direction: "rtl",
                                }}
                            >
                                <strong>
                                    {Number(product.price) > 0
                                        ? formatTomans(product.price)
                                        : "تماس بگیرید."}
                                </strong>
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </ListItem>
        );
    };

    render() {
        const { loadingVisible, productList, visibleGroups } = this.state;

        const containerStyle = {
            display: "flex",
            flexDirection: "row",
            overflowX: "auto",
            overflowY: "hidden",
            padding: "16px 8px",
            direction: "rtl",
            gap: 12,
        };

        return (
            <>
                {/* Splash overlay */}
                <LoadingSplash ref={this.loadingRef} />

                {/* Main content */}
                {!loadingVisible &&
                productList
                    .slice(0, visibleGroups) // 👈 NEW: lazy groups
                    .map((group, i) => {
                        const categoryTitle =
                            group?.category || "دسته‌بندی";
                        const firstProduct = group?.productList?.[0];
                        const categoryId = firstProduct?.category?.id;

                        const hrefAll = categoryId
                            ? `/product-list/?market_id=${this.props.market_id}&category_id=${categoryId}`
                            : "#";

                        return (
                            <Box key={i} sx={{ mb: 4, mx: 0.5 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        position: "relative",
                                        direction: "rtl",
                                        borderRadius: 3,
                                        border: "0px solid",
                                        borderColor: "divider",
                                        backgroundColor: "#fafafa",
                                        boxShadow:
                                            "0 6px 16px rgba(0,0,0,0.06)",
                                        p: 0,
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Header / category label + "more" icon */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            direction: "rtl",
                                            position: "relative",
                                            px: 0,
                                            pt: 0,
                                            pb: 0,
                                        }}
                                    >
                                        {/* Category ribbon */}
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                px: 1.5,
                                                height: 34,
                                                bgcolor: "#FF1744",
                                                color: "#fff",
                                                fontSize: 14,
                                                fontWeight: 800,
                                                borderRadius:
                                                    "0 0 14px 14px",
                                                boxShadow:
                                                    "0 2px 6px rgba(0,0,0,0.12)",
                                                lineHeight: 1,
                                                userSelect: "none",
                                                maxWidth: "75%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={categoryTitle}
                                        >
                                            {categoryTitle}
                                        </Box>

                                        {/* "More" button */}
                                        {categoryId && (
                                            <Tooltip
                                                title="مشاهده همه محصولات"
                                                arrow
                                                placement="top"
                                            >
                                                <IconButton
                                                    href={hrefAll}
                                                    sx={{
                                                        color: "#9a1111",
                                                        transition:
                                                            "all .2s ease",
                                                        border:
                                                            "1px solid rgba(154,17,17,0.25)",
                                                        backgroundColor:
                                                            "#fff",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#9a1111",
                                                            color: "#fff",
                                                            boxShadow:
                                                                "0 3px 8px rgba(154,17,17,0.3)",
                                                        },
                                                        width: 34,
                                                        height: 34,
                                                        mt: 1.5,
                                                        ml: 1.5,
                                                    }}
                                                >
                                                    <ArrowForwardIosRoundedIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            transform:
                                                                "scaleX(-1)",
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>

                                    {/* Horizontal product list (max 20 items) */}
                                    <div
                                        style={{
                                            ...containerStyle,
                                            marginTop: 0,
                                        }}
                                    >
                                        {group.productList
                                            .slice(0, 20)
                                            .map((product) =>
                                                this.renderProductCard(
                                                    product
                                                )
                                            )}
                                    </div>
                                </Paper>
                            </Box>
                        );
                    })}
            </>
        );
    }
}
